import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import { ShoppingBag, Heart, User, Search, Menu, X, LogOut, ShieldCheck, Briefcase } from 'lucide-react';
import { useCart } from '../context/CartContext'; 

const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pull setters and synchronization methods out of your custom hook
  const { getCartCount, wishlist, setCart, setWishlist, triggerAuthSync } = useCart(); 

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // AUTHENTICATION LOCAL STATES
  const [activeUser, setActiveUser] = useState(null);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Sync token state on mount and whenever user navigates back to check changes
  useEffect(() => {
    // FIXED: Swapped to pull from the unified safe memory object string instead of old profile key
    const savedUser = localStorage.getItem('modische_user');
    const token = localStorage.getItem('modische_token');
    
    // If explicit ghost values exist inside the client storage cache, handle as unsigned in safely
    if (savedUser && savedUser !== 'null' && savedUser !== 'undefined' && token) {
      try {
        setActiveUser(JSON.parse(savedUser));
      } catch (e) {
        console.error("Failed parsing auth profile cache.");
        setActiveUser({ name: 'Account' });
      }
    } else {
      setActiveUser(null);
    }
  }, [location]);

  // WIPE ALL USER DATA COMPLETELY ON SIGN-OUT
  const handleLogoutAction = () => {
    // FIXED: Completely flushes out both old keys and new standardized tokens to maintain perfect security locks
    localStorage.removeItem('modische_token');
    localStorage.removeItem('modische_user');
    localStorage.removeItem('modische_user_profile');
    
    if (setCart) setCart([]);
    if (setWishlist) setWishlist([]);
    if (triggerAuthSync) triggerAuthSync();

    setActiveUser(null);
    setIsUserDropdownOpen(false);
    navigate('/');
    window.location.reload(); // Refresh smoothly once to fully reset the App.js shield barriers
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/collection/search?query=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false); 
      setSearchQuery(''); 
    }
  };

  return (
    <header className="fixed top-0 w-full z-50">
      {/* PRIMARY NAVIGATION BAR */}
      <nav className="w-full bg-[#F5F5DC]/95 backdrop-blur-md px-8 py-5 border-b border-[#E5D3B3]/30">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          
          {/* LEFT: Menu Toggle */}
          <div className="flex items-center space-x-3 flex-1">
            <Menu 
              className="w-5 h-5 cursor-pointer text-[#2C1E12] hover:opacity-50 transition" 
              onClick={onMenuClick} 
            />
            <span 
              className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#2C1E12] cursor-pointer hidden sm:block"
              onClick={onMenuClick}
            >
              Menu
            </span>
          </div>

          {/* CENTER: Minimalist Text Title Branding */}
          <div className="flex-none text-center">
            <Link to="/" className="hover:opacity-80 transition block no-underline">
              {/* 🌟 DESKTOP DISPLAY: Full minimalist text wordmark */}
              <h1 className="text-2xl font-serif tracking-[0.1em] text-[#2C1E12] lowercase capitalize hidden sm:block">
                Modische Kledings
              </h1>
              
              {/* 🌟 MOBILE DISPLAY: High-end compact monogram token frees up your layout spacing lanes */}
              <h1 className="text-xl font-serif tracking-[0.2em] font-bold text-[#2C1E12] uppercase sm:hidden">
                MK
              </h1>
            </Link>
          </div>

          {/* RIGHT: E-Commerce Console Icons */}
          <div className="flex items-center space-x-5 flex-1 justify-end text-[#2C1E12]">
            
            {/* Toggle Search */}
            <button 
              onClick={() => setIsSearchOpen(!isSearchOpen)} 
              className="p-1 hover:opacity-50 transition bg-transparent border-none text-[#2C1E12]"
            >
              {isSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
            </button>
            
            {/* Wishlist Link (Kept 100% active on all screen widths!) */}
            <Link to="/wishlist" className="relative cursor-pointer hover:opacity-50 transition block">
              <Heart className="w-5 h-5" />
              {wishlist && wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-800 text-[#F5F5DC] text-[8px] w-4 h-4 flex items-center justify-center rounded-full font-bold shadow-sm">
                  {wishlist.length}
                </span>
              )}
            </Link>

            {/* Cart Link */}
            <Link to="/cart" className="relative cursor-pointer hover:opacity-50 transition block">
              <ShoppingBag className="w-5 h-5" />
              {getCartCount && getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#664229] text-[#F5F5DC] text-[8px] w-4 h-4 flex items-center justify-center rounded-full font-bold shadow-sm">
                  {getCartCount()}
                </span>
              )}
            </Link>
            
            <div className="h-4 w-[1px] bg-[#2C1E12]/20 mx-1" />
            
            {/* DYNAMIC RECOGNITION PROFILE LAYOUT */}
            {activeUser ? (
              <div className="relative">
                <button 
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-[#2C1E12] hover:opacity-70 transition bg-transparent border-none cursor-pointer"
                >
                  <User className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">
                    Hello, {activeUser.name ? activeUser.name.split(' ')[0] : 'Account'}
                  </span>
                  <span className="text-[7px] opacity-40 ml-0.5">▼</span>
                </button>

                {/* DROPDOWN MINI MODULE */}
                {isUserDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsUserDropdownOpen(false)} />
                    <div className="absolute right-0 mt-4 w-48 bg-[#F5F5DC] border border-[#E5D3B3] shadow-2xl rounded-sm py-2 z-20 text-left animate-fade-in">
                      <div className="px-4 py-2 border-b border-[#E5D3B3]/50 mb-1">
                        <p className="text-[8px] uppercase tracking-wider text-[#664229] opacity-60">Signed In As</p>
                        <p className="text-[10px] font-bold text-[#2C1E12] truncate">{activeUser.email || 'Verified Client'}</p>
                      </div>
                      
                      <button 
                        onClick={() => { navigate('/my-orders'); setIsUserDropdownOpen(false); }}
                        className="w-full px-4 py-2 text-[9px] uppercase tracking-widest font-black text-[#2C1E12] hover:bg-[#E5D3B3]/40 transition text-left flex items-center gap-2 cursor-pointer border-none bg-transparent"
                      >
                        <Briefcase size={12} /> Order History
                      </button>

                      {(activeUser.isAdmin === true || activeUser.role === 'admin') && (
                        <button 
                          onClick={() => { navigate('/admin'); setIsUserDropdownOpen(false); }}
                          className="w-full px-4 py-2 text-[9px] uppercase tracking-widest font-bold text-[#2C1E12] hover:bg-[#E5D3B3]/40 transition text-left flex items-center gap-2 cursor-pointer border-none bg-transparent"
                        >
                          <ShieldCheck size={12} /> Master Panel
                        </button>
                      )}

                      <button 
                        onClick={handleLogoutAction}
                        className="w-full px-4 py-2 text-[9px] uppercase tracking-widest font-bold text-red-900 hover:bg-red-50/5 transition text-left flex items-center gap-2 cursor-pointer border-none bg-transparent"
                      >
                        <LogOut size={12} /> Log Out Account
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Fallback profile link icon for guests */
              <Link to="/auth" className="hover:opacity-50 transition block text-[#2C1E12]">
                <User className="w-5 h-5" />
              </Link>
            )}

          </div>
        </div>
      </nav>

      {/* SEARCH DROPDOWN DRAWER */}
      {isSearchOpen && (
        <div className="w-full bg-[#E5D3B3] border-b border-[#2C1E12]/10 shadow-lg px-8 py-4 z-40">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSearchSubmit} className="relative flex items-center">
              <input 
                type="text"
                autoFocus
                placeholder="SEARCH BY PRODUCT NAME, COLOR, STYLE..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent border-b-2 border-[#2C1E12]/30 text-[#2C1E12] placeholder-[#2C1E12]/40 py-2 pl-2 pr-10 text-xs font-mono uppercase tracking-widest focus:outline-none focus:border-[#2C1E12] transition-colors"
              />
              <button 
                type="submit" 
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#2C1E12] opacity-60 hover:opacity-100 transition bg-transparent border-none"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;